import h from 'hyperscript';
import $ from 'jquery';
import type { IdTextPair, SearchOptions } from 'select2';
// @ts-expect-error - @types/select2 does not work for the select2.full.js
import select2 from 'select2/dist/js/select2.full.js';

import removeIcon from '@disy/cadenza-icons/remove.svg';
import { type StyleClass, addStyleClass } from '@disy/lib/add-style-class';
import { isOnWhiteBg } from '@disy/lib/colors';
import { assert, requireNonNullable } from '@disy/lib/custom-error';
import { ChangeEvent, on } from '@disy/lib/event-util';
import { isEqual } from '@disy/lib/is-equal';
import { logger } from '@disy/lib/logging';
import { px } from '@disy/lib/px';
import { type Teardown, tearDown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';
import type { Nullable } from '@disy/lib/type-util';
import { uniqueId } from '@disy/lib/unique-id';
import type { Content } from '@disy/ui/content';
import { FormElement } from '@disy/ui/form-element';
import { type FormGroup, type ValidationMessages } from '@disy/ui/form-group';
import { icon } from '@disy/ui/icon';
import '@disy/ui/menu-variables.css';
import { POPUP_PARENT_MARKER_CLASS, getPopupParent } from '@disy/ui/popup';
import '@disy/ui/search-input';

import { RESULTS_LIMIT, getDefaultResultAdapter } from 'ui/select/custom-results-adapter';
import { initializeMoreLess } from 'ui/select/select2-more-less-links';
import { select2Require } from 'ui/select/select2-require';
import {
  PLACEHOLDER_ID,
  PLACEHOLDER_TEXT,
  type SelectItem,
  type SelectOption,
  type SelectOptionGroup,
  type SelectQueryCallbackArgs,
  type SelectQueryParams,
  type SelectQueryType,
  type SelectSorterType,
  isGroupItem,
  isPlaceholderOption,
} from 'ui/select/select-types';

import { markSubstring } from 'cadenza/utils/mark-substring';
import { isPrimitive } from 'cadenza/utils/primitive';

import i18n from './messages.properties';
import './select.css';

select2($);

/**
 * The select2.full bundle that we use includes all modules. Hence, they are "required"/resolved
 * synchronously (not lazy loaded).
 * Those objects will be created only if the select2 object already exists (not loaded in the
 * tests for example)
 */
const Select2Utils = select2Require('select2/utils');
const Select2ArrayData = select2Require('select2/data/array');
const Select2AllowClear = select2Require('select2/selection/allowClear');
const Select2SelectionSearch = select2Require('select2/selection/search');

if (Select2SelectionSearch) {
  const previousBind = Select2SelectionSearch.prototype.bind;
  // Select2 removes listeners, which open the dropdown on ArrowDown.
  // We want the ArrowDown, so we prevent the listener from being removed.
  Select2SelectionSearch.bind = function (...params: unknown[]) {
    const result = previousBind.apply(this, params);
    this.$selection.off('input.searchcheck');
    return result;
  };
}

const COMPONENT_NAME = 'd-select';

interface MessagesType {
  loadingMore: () => string;
  maximumSelected: (options: { maximum: number }) => string;
  noResults: () => string;
  removeItem: () => string;
  searching: () => string;
  tooManyOptions: () => string;
  noMoreSelectableOptions: () => string;
}

const DEFAULT_MESSAGES: MessagesType = {
  loadingMore: () => i18n('loadMore'),
  maximumSelected: ({ maximum }) => i18n('selectionTooBig', { count: maximum }),
  noResults: () => i18n('noMatches'),
  removeItem: () => i18n('removeItem'),
  searching: () => i18n('searching'),
  tooManyOptions: () => i18n('tooManyOptions', { limit: RESULTS_LIMIT }),
  noMoreSelectableOptions: () => i18n('noMoreSelectableOptions'),
};

const DROPDOWN_MARGIN_BOTTOM = 12;
const MINIMUM_DROPDOWN_HEIGHT = 180;

type CustomContent = JQuery<Node> | Content | null;

/** A select box component's options. */
export interface SelectInitializationOptions<DATA = unknown> {
  /**
   * If <code>true</code>, displays a cross-shaped button inside the select that
   * clears the selection.
   */
  allowClear?: boolean;
  /** Function to validate tags creation */
  createTagFn?: (params: SearchOptions) => { id: string; text: string } | null;
  /** Data for options */
  data?: SelectItem<DATA>[];
  /** Value to reset to when select is embedded in form-group and "Reset" button is clicked.
   * Either ID if string, else the assigned data (assuming the generated id is unique) */
  defaultValue?: string | DATA;
  /** Whether the component is disabled. */
  disabled?: boolean;
  dropdownAdapter?: unknown;
  /** When true, tries to calculate width of dropdown to fit all items. */
  dropdownAutoWidth?: boolean;
  /**
   * Single: Show search in dropdown (default: false)
   * Multi: Use input field for search (default: true)
   */
  searchEnabled?: boolean;
  /** Adds additional CSS classes to the dropdown container. */
  dropdownStyleClass?: string;
  /** Element to append on the left side */
  elementLeft?: HTMLButtonElement;
  /** Element to append on the right side */
  elementRight?: HTMLButtonElement;
  /** Function used for custom search matching */
  matcher?: (params: SearchOptions, data: SelectItem<DATA>) => SelectItem<DATA> | null;
  /**
   * [maxSelection=0] - Multi-value select boxes can set restrictions regarding the
   * maximum number of options that can be selected.  If the value of this option is less than 1,
   * the number of selected items will not be limited.
   */
  maxSelection?: number;
  /** Feedback messages used by select. */
  messages?: Partial<MessagesType>;
  /** [minimumInputLength=0] - Minimum number of characters required to start a search. */
  minimumInputLength?: number;
  /**
   * [moreLessLinks=true] - If true and too many items are selected then overflow
   * will be hidden and show more button will be displayed
   */
  moreLessLinks?: boolean;
  /** [multiple=false] - If true, element becomes a multi-value select box (tags or pills). */
  multiple?: boolean;
  /** Specifies the placeholder for the control. */
  placeholder?: string | IdTextPair;
  /**
   * A function that gets called on every keystroke. Can be used to query
   * the server (throttled) to get options based on user input.
   */
  query?: SelectQueryType;
  /** [queryDelay=300] - Throttle time in ms. */
  queryDelay?: number;
  /**
   * If true no dropdown is shown. User is not permitted to add
   * more items. User can only remove given items.
   */
  removeOnly?: boolean;
  /**
   * A results adapter can modify results in a specific way, like limit the number
   * of results (see DefaultResultAdapter, UnlimitedResultAdapter)
   */
  resultsAdapter?: unknown;
  /** A value is required for the form to be submittable. */
  required?: boolean;
  /** Value for the "name" attribute of the contained HTMLSelectElement  */
  selectElementName?: string;
  /** Function that is used to sort the items */
  sorter?: SelectSorterType;
  /** Additional CSS classes to add to the element. */
  styleClass?: StyleClass;
  /**
   * In addition to a pre-populated menu of options, select can dynamically create new options from
   * text input by the user in the search box. This feature is called "tagging". To enable tagging,
   * set the tags option to `true`.
   */
  tags?: boolean | SelectOption<DATA>[];
  /** Customizes the way that search results are rendered */
  templateResult?: (item: SelectOption<DATA>, searchText: string, container: HTMLElement) => CustomContent;
  /** Customizes the way that search results group headers are rendered */
  templateGroupResult?: (item: SelectOptionGroup<DATA>, searchText: string, container: HTMLElement) => CustomContent;
  /** Customizes the way that selections are rendered. */
  templateSelection?: (selection: SelectOption<DATA>, container: HTMLElement) => CustomContent;
  /** The list of characters that should be used as token separators. */
  tokenSeparators?: string[];
  /** Validation messages that the select component should use. */
  validationMessages?: ValidationMessages;
}

/**
 * A select box component
 */
export class Select<DATA = unknown> extends FormElement<string[]> {
  readonly #select: HTMLSelectElement;
  readonly #hasQuery;
  readonly #elementLeft;
  readonly #elementRight;
  readonly #multiple;
  readonly #placeholder;
  readonly #searchInDropdown;
  readonly #tags: boolean | SelectOption<DATA>[];
  readonly #searchEnabled?: boolean;
  // Keep a reference to the scroll watchers for the dropdown
  #select2ScrollWatchers?: JQuery<HTMLElement>;

  #preventOpenDropdown = false; // Has to be false initially, otherwise dropdown won't open on first click
  #hasData;
  #moreLess: { update: () => void; enabled?: boolean; toggleHandler?: () => void } = {
    update() {
      /* overwrite */
    },
  };
  #teardowns: Teardown[] = [];

  constructor({
    allowClear = false,
    data = [],
    defaultValue,
    disabled = false,
    dropdownAdapter,
    dropdownAutoWidth,
    searchEnabled,
    dropdownStyleClass = '',
    elementLeft,
    elementRight,
    matcher,
    maxSelection,
    messages,
    minimumInputLength = 0,
    moreLessLinks = false,
    multiple = false,
    placeholder,
    query,
    queryDelay = 300,
    removeOnly = false,
    required = false,
    resultsAdapter = getDefaultResultAdapter(),
    selectElementName = '',
    sorter,
    styleClass,
    tags = false,
    templateGroupResult: customTemplateGroupResult,
    templateResult: customTemplateResult,
    templateSelection: customTemplateSelection,
    tokenSeparators,
    validationMessages,
    createTagFn = getDefaultCreateTagFn(tokenSeparators),
  }: SelectInitializationOptions<DATA> = {}) {
    super({
      defaultValue: defaultValue === undefined ? [] : [valueToString(defaultValue)],
      validationMessages: {
        badInput: validationMessages?.badInputMessage,
        patternMismatch: validationMessages?.patternMismatchMessage,
        rangeOverflow: validationMessages?.rangeOverflowMessage,
        rangeUnderflow: validationMessages?.rangeUnderflowMessage,
        stepMismatch: validationMessages?.stepMismatchMessage,
        tooLong: validationMessages?.tooLongMessage,
        tooShort: validationMessages?.tooShortMessage,
        typeMismatch: validationMessages?.typeMismatchMessage,
        valueMissing: validationMessages?.valueMissingMessage ?? i18n('valueMissingMessage'),
      },
    });
    this.#hasQuery = Boolean(query);
    this.#hasData = Boolean(data.length);
    this.#searchEnabled = searchEnabled;

    assert(!(this.#hasQuery && removeOnly), 'removeOnly and query cannot not be given at the same time.');
    assert(
      !customTemplateSelection || typeof customTemplateSelection === 'function',
      'formatSelection must be a function.',
    );
    assert(!customTemplateResult || typeof customTemplateResult === 'function', 'formatResult must be a function.');
    assert(
      !customTemplateGroupResult || typeof customTemplateGroupResult === 'function',
      'customTemplateGroupResult must be a function.',
    );
    assert(Array.isArray(data), 'data must be an array.');
    assert(!tokenSeparators || tags, 'tags must not be false if tokenSeparators is defined.');

    if (styleClass) {
      addStyleClass(this, styleClass);
    }

    this.#multiple = multiple;
    if (multiple) {
      adaptClearAll();
    }

    this.#moreLess.enabled = moreLessLinks;

    const select2data = data.filter((item) => item != null).map((item) => prepareValueForSelect2(item));

    // An (empty) placeholder is always required when allowClear is set for the multiple case,
    // because it's absence will provoke an NPE in select2
    this.#placeholder = multiple && allowClear && !placeholder ? '' : placeholder;

    // Empty option, because it is used for injecting placeholder
    this.#select = h(
      'select',
      { multiple, required, name: selectElementName },
      this.#placeholder ? createPlaceholderOption() : undefined,
    );

    messages = Object.assign({}, DEFAULT_MESSAGES, messages);

    if (validationMessages?.validationHint) {
      this.dataset.validationHint = validationMessages?.validationHint;
    }
    if (validationMessages?.validationMessage) {
      this.dataset.validationMessage = validationMessages?.validationMessage;
    }

    // Only triggered when using the constructor parameters (obsolete when using lit)
    if (disabled) {
      this.disabled = disabled;
    }
    // Only triggered when using the constructor parameters (obsolete when using lit)
    if (required) {
      this.required = required;
    }

    on(this.#select, ['invalid'], () => {
      this.classList.toggle('is-invalid', true);
    });

    const templateResult = (item: SelectItem<DATA>, container: HTMLElement) => {
      // do nothing for status message searching
      if (DEFAULT_MESSAGES.searching() === item.text) {
        return item.text;
      }

      const isGroup = isGroupItem(item);

      // for multiple selection, do not display already selected items in the dropdown
      if (!isGroup && this.#multiple && item.id && this.selection.find((element) => element.id === item.id)) {
        /*
         * Select2 renders also selected option. Select2 sets css to 'display: none' if we return null.
         * When we traverse over option highlight would disappear when hitting selected option.
         * To avoid this, we remove select2-results__option--selectable class.
         */
        container.classList.remove('select2-results__option--selectable');
        return null;
      }

      if (item.addSeparationLineBelow) {
        container.classList.add('select2-results__option--with-separation-line-below');
      }

      if (!isGroupItem(item) && item.disabled) {
        container.classList.toggle('select2-results__option--selected', this.selection[0].id === item.id);
      }

      const searchText = this.searchInput?.value ?? '';
      let result;
      if (customTemplateResult && !isGroup) {
        result = customTemplateResult(item, searchText, container);
      } else if (customTemplateGroupResult && isGroup) {
        result = customTemplateGroupResult(item, searchText, container);
      } else {
        result = defaultTemplateResult(item, searchText, container);
      }
      container.hidden = !isGroup && isPlaceholderOption(item); // Hide the empty placeholder from selection options
      return result;
    };

    const templateSelection = (item: SelectOption<DATA>, $container: JQuery) => {
      let content: CustomContent;
      if (isPlaceholderOption(item)) {
        content = [];
      } else if (customTemplateSelection && $container) {
        content = customTemplateSelection(item, $container[0]);
      } else {
        content = defaultTemplateSelection(item, $container);
      }

      if (multiple) {
        $container.find('.select2-selection__choice__display').addClass('ellipsis');
        const remove = $container.find('.select2-selection__choice__remove');
        remove.addClass(['button', 'button-icon', 'button-borderless', 'button-xs']).empty().append(icon(removeIcon));
        $container.append(remove); // Position remove button as last element in $container
        $container.addClass(hasWhiteTags(this) ? 'white-bg' : 'light-bg');
      }

      return content;
    };

    this.#searchInDropdown = !multiple && (searchEnabled || (searchEnabled == null && this.#hasQuery));

    // Chrome DevTools bug https://bugs.chromium.org/p/chromium/issues/detail?id=659515
    // we have to extract cssClass variables
    const selectionCssClass = `${COMPONENT_NAME}--selection`;
    const dropdownStyleClasses = [
      `${COMPONENT_NAME}--dropdown`,
      POPUP_PARENT_MARKER_CLASS,
      ...(dropdownStyleClass ? [dropdownStyleClass] : []),
      ...(removeOnly ? ['hidden'] : []),
      ...(this.#searchInDropdown ? [`${COMPONENT_NAME}--dropdown-search`] : []),
    ];
    let dataAdapter = this.#hasQuery ? createDataAdapter.call(this, removeOnly, query!, queryDelay) : Select2ArrayData;

    if (tags) {
      const Select2Tags = select2Require('select2/data/tags');
      dataAdapter = Select2Utils.Decorate(dataAdapter, Select2Tags);
      if (Array.isArray(tags)) {
        tags = tags.filter((item) => item != null).map((tag) => prepareValueForSelect2(tag)) as SelectOption<DATA>[];
      }
    }
    this.#tags = tags;

    if (tokenSeparators != null) {
      const Select2Tokenizer = select2Require('select2/data/tokenizer');
      dataAdapter = Select2Utils.Decorate(dataAdapter, Select2Tokenizer);
    }
    if (minimumInputLength > 0) {
      const MinimumInputLength = select2Require('select2/data/minimumInputLength');
      dataAdapter = Select2Utils.Decorate(dataAdapter, MinimumInputLength);
    }

    if (maxSelection != null && maxSelection > 0) {
      if (multiple) {
        const MaximumSelectionLength = select2Require('select2/data/maximumSelectionLength');
        dataAdapter = Select2Utils.Decorate(dataAdapter, MaximumSelectionLength);
      } else {
        // Do not show "maximum selection reached" message in the dropdown of single selects.
        maxSelection = undefined;
      }
    }

    $(this.#select)
      .appendTo(this)
      .select2({
        // @ts-expect-error @types/select2 are broken.
        ajax: this.#hasQuery ? true : undefined, // needed to enable pagination, although we use custom AJAX (see dataAdapter option),
        allowClear,
        // @ts-expect-error Select2 types are broken.
        data: select2data,
        dataAdapter,
        dropdownAdapter,
        resultsAdapter,
        dropdownCssClass: dropdownStyleClasses.join(' '),
        language: messages,
        // @ts-expect-error @types/select2 are broken.
        matcher,
        maximumSelectionLength: maxSelection,
        minimumInputLength,
        multiple,
        placeholder: this.#placeholder,
        selectionCssClass,
        selectOnClose: !!tags, // If 'tagging' (creation of new dynamic options) is allowed, create an option from the current text on focus lost. selectOnClose module is patched by Disy see Cadenza_Web_Frontend/patches/select2+<version>.patch
        // @ts-expect-error @types/select2 are broken.
        tags,
        // @ts-expect-error @types/select2 are broken.
        templateResult,
        // @ts-expect-error @types/select2 are broken.
        templateSelection,
        tokenSeparators,
        width: '100%',
        sorter,
        createTag: createTagFn,
        dropdownAutoWidth,
      });

    this._select2.on('results:message', (messageEvent: { message: string; args: { maximum?: number } }) => {
      const { message, args } = messageEvent;
      switch (message) {
        case 'maximumSelected':
          // Throws 'maximum-selected' event to be handled from outside when the select2 native 'results:message' with 'maximumSelected' message is thrown
          // This happens when there is maximum number of values defined (maxSelection) and reached.
          this.dispatchEvent(new CustomEvent('maximum-selected', { detail: args.maximum }));
      }
    });

    /*
     * h('select', options) sets the first option as selected even if  new Option(text, id, false, false) was set
     * select2 ignores placeholder if some option is set selected, therefore we have to add dummy option as first option and remove it after select2 is initialized
     */
    if (multiple && placeholder) {
      this.#select.firstElementChild?.remove();
    }

    if (elementLeft) {
      this.#elementLeft = elementLeft;
      elementLeft.classList.add(`${COMPONENT_NAME}--element-left`);
      this.append(elementLeft);
    }

    if (elementRight) {
      this.#elementRight = elementRight;
      elementRight.classList.add(`${COMPONENT_NAME}--element-right`);
      this.append(elementRight);
    }

    let dispatching = false;
    $(this.#select).on('change', (event) => {
      this.value = this.selection.map(({ id }) => id); // Update underlying form-element value

      const target = event.target as HTMLElement;
      if (target !== this) {
        // must be canceled explicitly
        event.stopImmediatePropagation();
      }
      if (target === this.#select) {
        this.classList.toggle('is-invalid', false);
        this.#moreLess.update();
        if (tags && multiple) {
          this._removeDuplicateTags();
        }
        if (!dispatching) {
          this.dispatchEvent(new ChangeEvent());
        }
      } else {
        // FormGroup identifies the accessibleSelect as control. For the resettable option
        // to work, dispatch a change event on the accessible #select.
        try {
          dispatching = true;
          this.#select.dispatchEvent(new ChangeEvent());
        } finally {
          dispatching = false;
        }
        return;
      }
    });

    const select = $(this.#select)
      // Unselecting opens DropDown. This is unwanted.
      .on('select2:unselect', () => {
        this._preventOpenDropdown();
      })
      .on('select2:opening', (event) => {
        if (this._isPreventOpenDropdown) {
          event.preventDefault();
        }
        // Wait for drop-down to load
        setTimeout(() => {
          const options = [...document.body.querySelectorAll('.select2-results__option')];
          const selectedOption = options.find((option) =>
            option.classList.contains('select2-results__option--selected'),
          );
          // Remove highlighting from other options if the currently selected item is disabled.
          if (selectedOption?.classList.contains('select2-results__option--disabled')) {
            const highlightedClass = 'select2-results__option--highlighted';
            options.find((option) => option.classList.contains(highlightedClass))?.classList.remove(highlightedClass);
          }
        });
      })
      .on('select2:open', () => {
        this.#updateDropdownVisibility();
        if (this.#searchInDropdown) {
          const searchInput = this.searchInput!;
          if (!searchInput.closest('d-search-input')) {
            const parent = searchInput.parentElement!;
            const dSearchInput = h('d-search-input', searchInput);
            on(
              dSearchInput,
              'keydown',
              (e) => {
                if (e.key !== 'Enter' && e.key !== 'Tab') {
                  // we only care about submitting keys
                  return;
                }
                // when the user a submitting key (enter or tab) while the focus is on the input field,
                // we want to choose the currently selected item.
                // However, when no item is available, we have to stop the event from reaching select2, because it will
                // break the clear button.
                // we're probably using the search input in combination with select2 wrong here, but decided to keep that fix
                // to avoid larger refactorings. Suspicion: select2 probably depends on the thought that hitting a submit key
                // will always close the dropdown (assuming an option was selected).
                const hasOptions = document.querySelector('.select2-results__option');
                if (!hasOptions) {
                  e.stopPropagation(); // do not propagate to select2
                }
              },
              {
                capture: true,
              },
            );
            parent.append(dSearchInput);
          }
          searchInput.focus();
        }

        const dropdownElem = document.querySelector(
          '.select2-container--default .select2-results > .select2-results__options',
        ) as HTMLElement;
        const dropdownAbove = document.querySelector('.select2-container--default .select2-dropdown--above');
        if (dropdownElem && !dropdownAbove) {
          // if not already above
          const dropdownTopPos = dropdownElem.getBoundingClientRect().top;
          const footerElem = document.querySelector('.d-page--footer');
          const footerHeight = footerElem ? footerElem.getBoundingClientRect().height : 15; // Fallback to the current default footer height
          const dropdownHeight = window.innerHeight - dropdownTopPos - footerHeight - DROPDOWN_MARGIN_BOTTOM;
          // If space below is too small and dropdown would end up being too small, don't apply the fix. Instead, the
          // dropdown will automatically be shown above of the select (where hopefully more space is available)
          if (dropdownHeight > MINIMUM_DROPDOWN_HEIGHT) {
            dropdownElem.style.maxHeight = px(dropdownHeight);
          }
        }
        // Same as in select2: get the "watchers".
        this.#select2ScrollWatchers = this._select2.$container.parents().filter(Select2Utils.hasScroll);
        this.dispatchEvent(new CustomEvent('dropdown-open'));
      })
      .on('select2:close', () => {
        // When close is dispatched, select2 should have removed the watchers itself.
        this.#select2ScrollWatchers = undefined;
      });

    // If we are using multiple selection, we need to reposition last chosen option
    // so that we have list of selected items in the order that we selected them.
    // Without this, order of the selected items would be the same of that in the menu.
    // https://github.com/select2/select2/issues/3106#issuecomment-539469984
    if (multiple) {
      select.on('select2:select', function (e) {
        const id = e.params.data.id;
        const option = $(e.target).children(`[value='${id}']`);
        $(e.target).append(option).trigger('change.select2');
      });
    }

    if (removeOnly) {
      this.searchInput!.parentElement!.hidden = true; // If removeOnly is true, we also have tags and a search input.
    }

    if (this.searchInput) {
      this.searchInput.readOnly = searchEnabled === false;
    }

    if (this.#multiple && this.#moreLess.enabled) {
      Object.assign(
        this.#moreLess,
        initializeMoreLess(this.#select, () => this.searchInput),
      );
      this.#moreLess.toggleHandler = () => {
        this.close();
      };
      this.#select.addEventListener('more-less-toggle', this.#moreLess.toggleHandler);
    }

    this.#bindShortcuts();

    // Initialize underlying form-element's value, even before we can access this.#select.selection.
    // We can't use this.selection because it builds upon <option> elements, which aren't rendered yet.
    this.setValue(data ? getSelectionFromData(data, placeholder) : []);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(COMPONENT_NAME);

    const whiteTags = hasWhiteTags(this);
    for (const el of this.querySelectorAll<HTMLElement>('.select2-selection__choice')) {
      el.classList.toggle('white-bg', whiteTags);
      el.classList.toggle('light-bg', !whiteTags);
    }

    const formGroup = this.closest<FormGroup>('form-group');
    if (formGroup) {
      this.#teardowns.push(on(formGroup, 'reset', () => this.reset()));
    }

    const select2 = this._select2;
    const dropdownParentElement = getPopupParent(this);
    assert(dropdownParentElement, 'DropdownParent options should not be null');
    select2.dropdown.$dropdownParent = $(dropdownParentElement!);

    // Select2 should handle "Escape" only if the dropdown is open.
    // Needed because select2 does not bubble the "Escape"
    on(
      this,
      ['keydown', 'keyup'],
      (event: KeyboardEvent) => {
        if (event.target !== this && event.key === 'Escape' && !this._select2.isOpen()) {
          event.stopImmediatePropagation();
          // prevent default if inside a popup to prevent closing ancestor dialogs
          if (this.closest('.d-popup')) {
            event.preventDefault();
          }
          this.dispatchEvent(new KeyboardEvent(event.type, { key: 'Escape', bubbles: true }));
        } else if (
          this.#searchEnabled === false &&
          !['ArrowUp', 'ArrowDown', 'Backspace', 'Enter'].includes(event.key)
        ) {
          // when the search is not enabled, allow only keyboard navigation
          event.stopImmediatePropagation();
        }
      },
      { capture: true },
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // If for some reason the select2 is detached before _detachPositioningHandler is called.
    this.#removeSelect2ScrollListeners();
    // Only dispatch 'close' after scroll listeners are removed, because on close event we clear the tracked parents.
    $(this.#select).select2('close');
    this.#teardowns = tearDown(this.#teardowns);
  }

  #bindShortcuts() {
    if (this.#multiple) {
      const search = this.searchInput!; // For multi-selection, the search input is present.
      search.addEventListener('keydown', (event: KeyboardEvent) => {
        // Force dispatch 'CTRL + ENTER'
        if (event.ctrlKey && event.key === 'Enter') {
          this.close();
          // Prevent Select2 handling keydown for this special case
          event.stopImmediatePropagation();
          // Select2 handles keyup. It will open the dropdown. In this special case we have to
          // prevent this behaviour
          $(search).one('keyup', (keyupEvent) => {
            keyupEvent.stopImmediatePropagation();
          });

          // Use CustomEvent because IE does not support KeyboardEvent.
          this.dispatchEvent(
            Object.assign(new CustomEvent('keydown', { bubbles: true }), {
              key: 'Enter',
              ctrlKey: true,
              keyCode: 13,
              location: 0,
              code: 'Enter',
              charCode: 0,
              which: 13,
            }),
          );
        }
      });
    }
  }

  #getDropdownVisibility() {
    // if we want to use the select component without selection functionality, we hide the dropdown
    if (!!this.#tags && !this.#hasData && !this.#hasQuery) {
      return false;
    }
    if (this.#hasQuery) {
      return true;
    }
    if (!this.#hasData) {
      return false;
    }
    /* We always want to show the dropdown:
     - In both cases, if there are options available to select, we need to show the dropdown for users to be able to choose them.
     - But we also want to show the dropdown even if there are no more options available to select
       For single-select, even if there's only one option available (and it's already selected).
       For multi-select, if all options are selected and there is nothing left to select, we will be showing a "No results" info message. */
    return true;
  }

  #updateDropdownVisibility() {
    let showDropdown = this.#getDropdownVisibility();
    this._select2.dropdown.$dropdown.toggleClass('hidden', !showDropdown);
  }

  open() {
    $(this.#select).select2('open');
  }

  close() {
    $(this.#select).select2('close');
  }

  override focus() {
    this._select2.focus();
  }

  override applyValue(value: string[]) {
    // Because we have both, this.selection and this.value, we must ensure both can be used and update each other
    // without infinite recursion. It might be possible to merge both APIs, so that we only have this.value from
    // form-elements (not trivial).
    const currentValue = this.selection.map(({ id }) => id);
    if (isEqual(currentValue, value)) {
      return;
    }

    this.selection = value;
  }

  /**
   * Get the selection
   *
   * @return The selected options
   */
  get selection(): SelectOption<DATA>[] {
    return $(this.#select).select2('data') as unknown as SelectOption<DATA>[];
  }

  /**
   * Sets options based on given data.
   *
   * Options will be set based on given data. If data is a string or an array of strings
   * then it is seen as ID or a flat array of IDs otherwise data must contain all
   * relevant option data.
   *
   * @param data - ID or array of IDs or option data or array of option data
   */
  set selection(data: unknown | SelectOption<DATA> | (unknown | SelectOption<DATA>)[]) {
    const preparedData = this.#prepareData(data);
    if (!preparedData) {
      return;
    }

    for (const option of preparedData) {
      const sanitizedOptionId = option.id.includes("'") ? option.id.replaceAll(/'/g, "\\'") : option.id;
      let optionElement = this.#select.querySelector<HTMLOptionElement>(`option[value='${sanitizedOptionId}']`);
      if (!optionElement) {
        // Note: the element returned has 'selected' set to false, always.
        optionElement = createOptionForSelect2(option);
        this.#select.add(optionElement);
      }
      // If the option is already selected, the flag needs to be updated here. Only set
      // if necessary, undefined means 'not selected'.
      if (option.selected) {
        optionElement.selected = true;
      }
    }

    if (this.#hasData) {
      const options = [...this.#select.getElementsByTagName('option')];
      const selectedOptions: HTMLOptionElement[] = [];
      const notSelectedOptions: HTMLOptionElement[] = [];
      options.forEach((option) => {
        const selected = preparedData.some((item) => item.id === option.value);
        option.selected = selected;
        if (selected) {
          selectedOptions.push(option);
        } else {
          notSelectedOptions.push(option);
        }
      });
      const reorderedSelectedOptions = this.#calculateReorderedOptions(preparedData, selectedOptions);
      if (reorderedSelectedOptions) {
        options.forEach((option) => this.#select.removeChild(option));
        reorderedSelectedOptions.forEach((option) => this.#select.appendChild(option));
        notSelectedOptions.forEach((option) => this.#select.appendChild(option));
      }
      // Programmatically change of options property selected does not notify select2 about changes
      $(this.#select).trigger('change.select2'); // Notify only Select2 of changes
    } else {
      this.#select.innerHTML = '';
      if (this.#multiple && this.#placeholder) {
        this.#select.add(createPlaceholderOption()); // Empty option, because it is used for injecting placeholder
      }
      preparedData.forEach((item) => {
        const newOption = new Option(item.text, item.id, true, true);
        // Have the selection status in the element's data up to date as well. This is important
        // for the algorithm that counts selected options in the custom reasults adapter
        item.selected = true;
        Select2Utils.StoreData(newOption, 'data', item);
        this.#select.add(newOption);
      });
    }

    // Update underlying FormElement value
    this.value = this.selection.map(({ id }) => id);
  }

  #prepareData(data: unknown): SelectOption<DATA>[] | undefined {
    if (!this.#multiple && !isPrimitive(data) && Array.isArray(data) && data.length > 1) {
      logger.error('Single select cannot set more than one value');
      return;
    }

    if (!Array.isArray(data)) {
      data = [data];
    }
    return (data as unknown[]).filter((item) => item != null).map(prepareValueForSelect2) as SelectOption<DATA>[];
  }

  setData(data: SelectItem<DATA>[]) {
    this.#hasData = Boolean(data.length);
    $(this.#select).val(''); // clean up current selection
    this.#select.innerHTML = ''; // clean up current data (<option> / <optGroup> elements)

    data
      .filter((item) => item != null)
      .map((item) => {
        const preparedItem = prepareValueForSelect2(item);
        return isGroupItem(preparedItem) ?
            createOptionGroupForSelect2(preparedItem)
          : createOptionForSelect2(preparedItem);
      })
      .forEach((item) => this.#select.add(item));

    if (data) {
      this.setValue(getSelectionFromData(data, this.#placeholder));
    }
  }

  getData() {
    const optionElements = [...this.#select.querySelectorAll('option')];
    return optionElements.map((option) => {
      return {
        id: requireNonNullable(option.dataset.select2Id),
        text: option.textContent,
      };
    });
  }

  // In multiple selection when selecting options programmatically, options should appear
  // in the same order as passed in data. If they are already in a proper order, then we return false
  // here, because there is no need to do anything
  #calculateReorderedOptions(data: SelectOption[], selectedOptions: HTMLOptionElement[]) {
    let shouldReorder = false;
    const reorderedSelectedOptions: HTMLOptionElement[] = [];
    if (selectedOptions.length > 0) {
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const option = selectedOptions[i];
        if ((row.id || row) === option.value) {
          reorderedSelectedOptions.push(option);
        } else {
          const reorderedOption = selectedOptions.find((o) => (row.id || row) === o.value);
          if (reorderedOption) {
            shouldReorder = true;
            reorderedSelectedOptions.push(reorderedOption);
          }
        }
      }
    }

    return shouldReorder ? reorderedSelectedOptions : false;
  }

  override checkValidity() {
    return !this.classList.contains('is-invalid');
  }

  override get validity() {
    return this.#select.validity;
  }

  protected override createValidity() {
    const validity = super.createValidity();
    validity.valueMissing = this.required && this.value.length === 1 && this.value[0] === PLACEHOLDER_ID;
    return {
      ...validity,
      customError: validity.customError ? this.dataset.validationMessage : undefined,
    };
  }

  override get disabled() {
    return super.disabled;
  }

  override set disabled(value) {
    super.disabled = value;

    if (this.#elementLeft) {
      this.#elementLeft.disabled = value;
    }

    if (this.#elementRight) {
      this.#elementRight.disabled = value;
    }

    const querySelector = this.querySelector('.d-select2--more-less') as HTMLButtonElement;
    if (querySelector) {
      querySelector.disabled = value;
    }

    this.#select.disabled = value;
  }

  override set required(value: boolean) {
    super.required = value;
    this.#select.required = value;
  }

  override get required() {
    return this.#select.required;
  }

  get labelledBy(): string | null {
    return this.referenceTarget.getAttribute('aria-labelledby');
  }

  set labelledBy(elementOrId: Nullable<Element | string>) {
    if (elementOrId instanceof Element && !elementOrId.id) {
      elementOrId.id = uniqueId();
    }

    const id = typeof elementOrId === 'string' ? elementOrId : elementOrId?.id;
    const target = this.referenceTarget;
    if (id) {
      target.setAttribute('aria-labelledby', id);
    } else {
      target.removeAttribute('aria-labelledby');
    }
  }

  get referenceTarget() {
    return (!this.#searchInDropdown && this.searchInput) || this.accessibleSelect;
  }

  get describedBy() {
    return this._selection.getAttribute('aria-describedby');
  }

  set describedBy(elementOrId) {
    const id = elementOrId?.id ?? elementOrId;
    if (id) {
      this._selection.setAttribute('aria-describedby', id);
    } else {
      this._selection.removeAttribute('aria-describedby');
    }
  }

  get searchInput(): HTMLInputElement | undefined {
    if (this.#searchInDropdown) {
      return this._select2.dropdown.$search?.[0];
    }
    return this._select2.selection.$search?.[0];
  }

  get accessibleSelect() {
    return this.#select;
  }

  /**
   * Returns the ID of the element with the combobox role.
   *
   * @return The ID
   */
  get comboboxId() {
    if (!this._selection.id) {
      this._selection.id = uniqueId();
    }
    return this._selection.id;
  }

  // ============= Private ============ //

  get _selection() {
    return this._select2.$selection[0];
  }

  get _select2() {
    return $(this.#select).data().select2;
  }

  _preventOpenDropdown() {
    this.#preventOpenDropdown = true;
    setTimeout(() => {
      this.#preventOpenDropdown = false;
    });
  }

  get _isPreventOpenDropdown() {
    return this.#preventOpenDropdown;
  }

  /*
   * There is a bug in select2 which causes 2 tags being created when user types something and presses Enter.
   * Precondition: tags and multiple enabled, data or query also available.
   * Reproduce: Assume there is one option available: "Apple". If user types "app" and adds it,
   * tag "app" is created. If the user types "app" again, dropdown shows only "Apple". If user presses
   * enter then 2 tags are created: "app" and "Apple". This results in duplicated tag "app".
   * Problem seems to be with select2:
   * https://github.com/select2/select2/issues/3498
   * https://github.com/select2/select2/issues/5861
   *
   * This is fix for our case: we know we cannot have duplicated email addresses, so we can safely
   * take last dynamically created tag and check whether there is another one with same value.
   * In case we have match we remove the last one. Other tags which are created by selecting
   * option from dropdown don't have selector: "[data-select2-tag="true"]".
   */
  _removeDuplicateTags() {
    const newTags = [...this.#select.querySelectorAll('option[data-select2-tag="true"]')] as HTMLOptionElement[];
    const lastTag = newTags.pop();
    if (newTags.length > 0 && lastTag && newTags.some((tag) => tag.value === lastTag.value)) {
      lastTag.remove();
    }
  }

  /**
   * If the select is initialized with the 'query' option, this is the way to programmatically trigger a query.
   * If this select contains a searchInput, its value is included in the query.
   */
  executeQuery() {
    if (this.#hasQuery) {
      let term;
      if (this.searchInput) {
        term = $(this.searchInput).val();
      }
      this._select2.trigger('query', { term });
    }
  }

  override get name() {
    return this.#select.name;
  }

  override set name(value) {
    this.#select.name = value;
  }

  /**
   * Select2 disables scrolling when a select list is openend to keep the select and the select list together.
   * In some cases, the Select2 element is removed from the DOM before it can remove its scroll listeners
   * from its parents - then scrolling will remain prevented because it then cannot find its parents.
   */
  #removeSelect2ScrollListeners() {
    const scrollEvent = 'scroll.select2.' + this._select2.id;
    const resizeEvent = 'resize.select2.' + this._select2.id;
    const orientationEvent = 'orientationchange.select2.' + this._select2.id;
    this.#select2ScrollWatchers?.off(scrollEvent);
    $(window).off(scrollEvent + ' ' + resizeEvent + ' ' + orientationEvent);
  }
}

customElements.define(COMPONENT_NAME, Select);

function valueToString<DATA>(value: string | DATA) {
  return String(value);
}

function hasWhiteTags<DATA>(select: Select<DATA>): boolean {
  return isOnWhiteBg(select) && !select.disabled;
}

function defaultTemplateSelection(item: SelectOption, $container: JQuery) {
  const content = [];

  // The container might be missing if select2 is initialized with a user defined placeholder and no selected items
  $container?.addClass('default-layout');
  if (item.icon) {
    content.push(item.icon.cloneNode(true));
  }
  content.push(h('span.ellipsis', item.text));

  return content;
}

function defaultTemplateResult(item: SelectItem, searchText: string, container: HTMLElement) {
  container.classList.add('default-layout');
  const iconElement = (item as SelectOption).icon?.cloneNode(true);
  const isGroupName = container.classList.contains('select2-results__group');
  const text = h(
    `span${isGroupName ? '' : '.ellipsis'}`,
    searchText ? markSubstring(item.text, searchText) : item.text,
  );
  return iconElement ? [iconElement, text] : text;
}

function getDefaultCreateTagFn(tokenSeparators?: string[]) {
  return (params: { term: string }) => {
    const value = params.term.trim();
    return !(value === '' || tokenSeparators?.some((separator) => value === separator)) ?
        { id: value, text: value }
      : null;
  };
}

function adaptClearAll() {
  // This is a hack of the select2 AllowClear prototype. The proper way would be to use the
  // Decorator mechanism, unfortunately that seems to fail. The decorated method is never
  // called. Since we have no other places where we use the built-in feature, this hack is
  // the workaround.

  Select2AllowClear.prototype.update = function (decorated: (data: unknown[]) => void, data: unknown[]) {
    decorated.call(this, data);

    this.$selection.find('.select2-selection__clear').remove();

    if (this.$selection.find('.select2-selection__placeholder').length > 0 || data.length === 0) {
      return;
    }

    let selectionId = this.$selection.find('.select2-selection__rendered').attr('id');

    let removeAll = this.options.get('translations').get('removeAllItems');

    // This button is the reason the method was overwritten. The button provided by select2
    // can not simply be manipulated, as it is removed and re-added several times during one
    // selection.
    const remove = h<HTMLButtonElement>(
      'button.button.button-xs.button-icon.select2-selection__clear',
      {
        attrs: {
          title: removeAll(),
          'aria-label': removeAll(),
          'aria-describedby': selectionId,
          tabindex: '0',
        },
      },
      icon(removeIcon),
    );
    remove.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this._handleClear(event);
    });
    remove.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this._handleClear(event);
      }
    });

    Select2Utils.StoreData($(remove)[0], 'data', data);

    this.$selection.prepend($(remove));
  };
}

// Is called with the select component as this.
function createDataAdapter(removeOnly: boolean, query: SelectQueryType, queryDelay: number) {
  function CustomData($element: JQuery, options: Record<string, unknown>) {
    // @ts-expect-error TS does not get the Select2Utils.Extend
    CustomData.__super__.constructor.call(this, $element, options);
  }

  Select2Utils.Extend(CustomData, Select2ArrayData);

  if (removeOnly) {
    CustomData.prototype.query = (params: SelectQueryParams, callback: (args: SelectQueryCallbackArgs) => void) => {
      callback({ results: [] });
    };
  } else {
    let currentCallback: ((args: SelectQueryCallbackArgs) => void) | null;
    // `params` has this structure: `{term: "aa", _type: "query", page: 1}` where `term` is the user input
    const throttledQuery = throttle((params: SelectQueryParams, callback: (args: SelectQueryCallbackArgs) => void) => {
      currentCallback = callback; // only latest request must be shown
      if (!params.page) {
        // initial request page number is undefined
        params.page = 1;
      }
      query(params, (data: SelectQueryCallbackArgs) => {
        if (currentCallback === callback) {
          currentCallback = null; // due to garbage-collector
          callback(data);
        } else {
          logger.warn('Response of a previous query was ignored. Obsolete queries should be aborted.');
        }
      });
    }, queryDelay);
    // Cannot be inlined into wrappedQuery, because of throttling

    /**
     * Get a set of options that are filtered based on the parameters that have been passed on in. Doc from:
     * https://select2.org/advanced/adapters-and-decorators#dataadapter
     *
     * @param params - An object containing any number of parameters that the query could be affected by. Only the core
     * parameters will be documented.
     * @param params.term - A user-supplied term. This is typically the value of the search box, if one exists, but can
     * also be an empty string or null value.
     * @param params.page - The specific page that should be loaded. This is typically provided when working with remote
     * data sets, which rely on pagination to determine what objects should be displayed.
     * @param callback - The function that should be called with the queried results.
     */
    CustomData.prototype.query = function (
      this: Select,
      params: SelectQueryParams,
      callback: (args: SelectQueryCallbackArgs) => void,
    ) {
      if (!this._isPreventOpenDropdown) {
        throttledQuery(params, callback);
      }
    };
  }
  return CustomData;
}

/**
 * Function that formats value so that it can be used with select2. It checks whether the value
 * is a primitive, and if that's the case returns object that has the same id and text as the value
 * of the element.
 *
 * @param value - Value that should be used with select2.
 * @return - Returns the value that was parsed to OptionData format.
 */
function prepareValueForSelect2(value: unknown): SelectItem & { _resultId: string } {
  if (isPrimitive(value)) {
    const valueString = valueToString(value);
    return {
      id: valueString,
      text: valueString,
      _resultId: valueString, // workaround for screenreader accessibility CADENZA-14735
      data: value,
    };
  }
  const item = value as ReturnType<typeof prepareValueForSelect2>;
  if ('id' in item) {
    item._resultId = item.id;
    item.selected = (value as SelectOption).selected;
  }
  return item;
}

function createOptionForSelect2(selectOption: SelectOption) {
  const element = new Option(selectOption.text, selectOption.id, false, selectOption.selected);
  Select2Utils.StoreData(element, 'data', selectOption);
  return element;
}

function createOptionGroupForSelect2(selectOptionGroup: SelectOptionGroup & { _resultId: string }) {
  const element = h<HTMLOptGroupElement>(
    'optgroup',
    { label: selectOptionGroup.text || selectOptionGroup },
    selectOptionGroup.children.map(createOptionForSelect2),
  );
  Select2Utils.StoreData(element, 'data', selectOptionGroup);
  return element;
}

function createPlaceholderOption() {
  return new Option(PLACEHOLDER_TEXT, PLACEHOLDER_ID);
}

function getSelectionFromData<DATA = unknown>(
  items: SelectItem<DATA>[],
  placeholder: string | IdTextPair | undefined,
): string[] {
  const selection: string[] = [];

  // Loop through items (We either see items or groups that contain only items)
  for (const item of items) {
    if (isGroupItem(item)) {
      // Handle group
      for (const child of item.children) {
        if (child.selected) {
          selection.push(child.id);
        }
      }
    } else {
      // Handle single option
      if (item.selected) {
        selection.push(item.id);
      }
    }
  }

  // Ensure we select the placeholder when nothing else is selected and a placeholder was defined
  if (selection.length === 0 && placeholder) {
    selection.push(PLACEHOLDER_ID);
  }

  return selection;
}
